import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import type { RecentChangesResponse } from "@/gql/graphql";
import { transformApolloError } from "@/lib/error/error-utils";
import { useSalonStore } from "@/lib/store/salon";
import { GET_RECENT_CHANGES } from "../graphql/audit";

export interface UseAuditQueryOptions {
  salonId?: string | null;
  limit?: number;
  tableNames?: string[];
  userId?: string | null;
  entityId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export const useAuditQuery = ({
  salonId,
  limit = 20,
  tableNames,
  userId,
  entityId,
  startDate,
  endDate,
}: UseAuditQueryOptions = {}) => {
  const { currentSalon } = useSalonStore();

  // Handle passed value or fallback to store
  const finalSalonId = salonId !== undefined ? salonId : currentSalon?.id || null;

  const {
    data,
    loading,
    error: apolloError,
    refetch,
  } = useQuery<{
    getRecentChanges: RecentChangesResponse;
  }>(GET_RECENT_CHANGES, {
    variables: {
      salonId: finalSalonId,
      limit,
      tableNames,
      userId,
      entityId,
      startDate,
      endDate,
    },
    skip: finalSalonId === null,
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
  });

  const error = useMemo(() => {
    if (!apolloError) return null;
    return transformApolloError(apolloError);
  }, [apolloError]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  const auditEntries = useMemo(() => {
    return data?.getRecentChanges?.entries || [];
  }, [data?.getRecentChanges?.entries]);

  const totalCount = useMemo(() => {
    return data?.getRecentChanges?.totalCount || 0;
  }, [data?.getRecentChanges?.totalCount]);

  return {
    data: auditEntries,
    totalCount,
    loading,
    error,
    retry,
    refetch,
  };
};
